
import React from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { TimeFrame } from "@/services/analytics/metricsService";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

interface PromptTimeFrameSelectorProps {
  timeFrame: TimeFrame;
  onTimeFrameChange: (timeFrame: TimeFrame) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
}

const PromptTimeFrameSelector: React.FC<PromptTimeFrameSelectorProps> = ({
  timeFrame,
  onTimeFrameChange,
  dateRange,
  onDateRangeChange
}) => {
  return (
    <div className="flex flex-wrap items-center gap-2 my-2">
      <div className="flex items-center space-x-1">
        <Button
          variant={timeFrame === "daily" ? "default" : "outline"}
          size="sm"
          onClick={() => onTimeFrameChange("daily")}
        >
          Daily
        </Button>
        <Button
          variant={timeFrame === "weekly" ? "default" : "outline"}
          size="sm"
          onClick={() => onTimeFrameChange("weekly")}
        >
          Weekly
        </Button>
        <Button
          variant={timeFrame === "monthly" ? "default" : "outline"}
          size="sm"
          onClick={() => onTimeFrameChange("monthly")}
        >
          Monthly
        </Button>
      </div>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={timeFrame === "custom" ? "default" : "outline"}
            size="sm"
            className="ml-auto"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {timeFrame === "custom" && dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "LLL dd, y")} -{" "}
                  {format(dateRange.to, "LLL dd, y")}
                </>
              ) : (
                format(dateRange.from, "LLL dd, y")
              )
            ) : (
              "Custom Range"
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={(range) => {
              onDateRangeChange(range);
              if (range?.from) {
                onTimeFrameChange("custom");
              }
            }}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
      
      {timeFrame === "custom" && dateRange?.from && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => {
            onTimeFrameChange("monthly");
            onDateRangeChange(undefined);
          }}
        >
          Reset
        </Button>
      )}
    </div>
  );
};

export default PromptTimeFrameSelector;
